import React, { useState, useEffect } from "react";
import { normalizeTags } from "../utils/tagUtils";
import { getActiveBoosterDisplay } from "../utils/boosterMetadata";
import { InAppNotificationBell } from "./InAppNotificationBell";
import { 
  Flame, 
  Sparkles, 
  Target, 
  Compass, 
  Save, 
  CheckSquare, 
  Trophy, 
  ArrowRight, 
  Star, 
  Eye, 
  ExternalLink, 
  Calendar, 
  MapPin, 
  Search, 
  ChevronRight, 
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Zap, 
  FolderDot,
  CheckCircle,
  Briefcase,
  Megaphone,
  Clock,
  ThumbsUp,
  AlertCircle,
  Lock,
  X
} from "lucide-react";
import { BusinessBusiness } from "../types";
import { 
  getUserStats, 
  getUserStats as reloadStats,
  saveUserStats,
  getPreferredCategories, 
  getPersonalizedScore, 
  getPersonalizedExplanation,
  getSuggestedRoles,
  getHiringLikelihood,
  getWhyWorthContacting,
  incrementDailyGoal,
  UserStats,
  trackQuestCompletion,
  trackBoosterMinutes,
  getAchievementProgressValue
} from "../utils/opportunityHelpers";
import { analyzeBusinessProspect } from "../utils/opportunityFinder";
import { bizDebug, bizError } from "../utils/debugLogger";
import { TodaysScoutingQuestSection } from "./TodaysScoutingQuestSection";

interface DashboardViewProps {
  currentUser: any;
  savedBusinesses: BusinessBusiness[];
  Businesses: BusinessBusiness[];
  apiFetch: any;
  onSelectBusiness: (id: string) => void;
  onSetWorkspaceMode: (mode: "search" | "saved" | "jobs" | "pro_tools" | "history" | "docs" | "admin_cost" | "dashboard") => void;
  onTriggerSearch: (keyword: string, location: string, tagsFilter?: string[]) => void;
  settingsAppTheme?: string;
  isProUser?: boolean;
  onUpgradeClick?: () => void;
  onBillingChange?: () => void;
  showToast?: (message: string, type?: "success" | "info" | "error") => void;
}

export default function DashboardView({
  currentUser,
  savedBusinesses,
  Businesses,
  apiFetch,
  onSelectBusiness,
  onSetWorkspaceMode,
  onTriggerSearch,
  settingsAppTheme = "light",
  isProUser = false,
  onUpgradeClick,
  onBillingChange,
  showToast
}: DashboardViewProps) {
  const [stats, setStats] = useState<UserStats>(getUserStats(currentUser?.id));
  const [preferredCats, setPreferredCats] = useState<Record<string, number>>({});
  const [isBadgesModalOpen, setIsBadgesModalOpen] = useState(false);
  const [badgeFilter, setBadgeFilter] = useState<"all" | "unlocked" | "locked">("all");
  const [badgeSearch, setBadgeSearch] = useState("");

  // BizNearYou Quest System State
  const [apiError, setApiError] = useState<string | null>(null);
  const [quests, setQuests] = useState<any[]>([]);
  const [questStats, setQuestStats] = useState<any | null>(null);
  const [questsLoading, setQuestsLoading] = useState<boolean>(true);
  const [claimingQuestKey, setClaimingQuestKey] = useState<string | null>(null);
  const [activeQuestTab, setActiveQuestTab] = useState<"all" | "daily" | "weekly" | "one_time" | "pro">("all");
  const [boosterTimeRemaining, setBoosterTimeRemaining] = useState<string | null>(null);
  const [expandedQuestId, setExpandedQuestId] = useState<string | null>(null);
  const [mysteryBoosterAward, setMysteryBoosterAward] = useState<{ message: string; minutes: number } | null>(null);

  const loadQuests = () => {
    setQuestsLoading(true);
    bizDebug("QUEST_LOAD_START");
    apiFetch("/api/quests")
      .then((res: any) => {
        if (!res.ok) {
          if (res.status !== 401 && res.status !== 403 && res.status !== 400) {
            bizError("QUEST_LOAD_ERROR", { status: res.status, url: "/api/quests" });
          }
          throw new Error(`Server returned status ${res.status}`);
        }
        return res.json();
      })
      .then((data: any) => {
        if (data.success) {
          const loadedQuestsList = data.quests || [];
          const loadedQuestStatsVal = data.stats || null;
          setQuests(loadedQuestsList);
          setQuestStats(loadedQuestStatsVal);
          
          if (loadedQuestStatsVal && loadedQuestStatsVal.total_points !== undefined) {
            const currentLocalStats = getUserStats(currentUser?.id);
            currentLocalStats.total_points = loadedQuestStatsVal.total_points;
            saveUserStats(currentLocalStats, currentUser?.id);
            setStats(currentLocalStats);
          }

          bizDebug("QUEST_LOAD_SUCCESS", {
            questCount: loadedQuestsList.length,
            hasStats: !!loadedQuestStatsVal,
            proBoosterUntil: loadedQuestStatsVal?.pro_booster_until
          });
        } else {
          bizError("QUEST_LOAD_ERROR", data.error || "success flag was false");
          setApiError(data.error || "Failed to load quests: success flag was false");
        }
      })
      .catch((err: any) => {
        if (err.message !== "Server returned status 401" && err.message !== "Server returned status 403" && err.message !== "Server returned status 400") {
          bizError("QUEST_LOAD_EXCEPTION", err.message || err);
        }
        setApiError("Failed to retrieve Scouting Quests: " + (err.message || err));
        console.warn("Failed to load quests:", err);
      })
      .finally(() => setQuestsLoading(false));
  };

  const claimQuest = (questKey: string) => {
    if (claimingQuestKey) return;

    const targetQuest = quests.find(q => q.quest_key === questKey);
    if (targetQuest?.is_claimed) return;

    setClaimingQuestKey(questKey);

    // Optimistic UI update
    setQuests(prevQuests =>
      prevQuests.map(q =>
        q.quest_key === questKey
          ? { ...q, is_claimed: true, is_completed: true, is_complete: true, claimed_at: new Date().toISOString() }
          : q
      )
    );

    apiFetch("/api/quests/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ questKey })
    })
      .then((res: any) => res.json())
      .then((data: any) => {
        if (data.success) {
          const awardedPoints = data.result?.points_awarded || targetQuest?.points_reward || 20;

          trackQuestCompletion();
          if (data.result?.random_booster_minutes > 0) {
            trackBoosterMinutes(data.result.random_booster_minutes);
            setMysteryBoosterAward({
              message: data.result.random_booster_message,
              minutes: data.result.random_booster_minutes
            });
          }

          const currentLocalStats = getUserStats(currentUser?.id);
          if (data.result?.total_points !== undefined) {
            currentLocalStats.total_points = data.result.total_points;
          } else if (awardedPoints > 0) {
            currentLocalStats.total_points = (currentLocalStats.total_points || 0) + awardedPoints;
          }
          saveUserStats(currentLocalStats, currentUser?.id);
          setStats(currentLocalStats);

          window.dispatchEvent(new CustomEvent("biznearyou_quests_updated", { detail: data }));

          loadQuests();

          if (showToast) {
            if (data.result?.already_claimed) {
              showToast(`Quest "${targetQuest?.name || "Scouting Quest"}" is already claimed.`, "info");
            } else {
              showToast(`🎉 Quest Claimed: "${targetQuest?.name || "Scouting Quest"}"! +${awardedPoints} XP awarded!`, "success");
            }
          }

          if (onBillingChange) {
            onBillingChange();
          }
        } else {
          console.error("Failed to claim quest:", data.error);
          loadQuests();
        }
      })
      .catch((err: any) => {
        console.error("Error claiming quest:", err);
        loadQuests();
      })
      .finally(() => {
        setClaimingQuestKey(null);
      });
  };

  useEffect(() => {
    loadQuests();

    const handleQuestsUpdated = () => {
      loadQuests();
    };

    window.addEventListener("biznearyou_quests_updated", handleQuestsUpdated);
    return () => {
      window.removeEventListener("biznearyou_quests_updated", handleQuestsUpdated);
    };
  }, []);

  useEffect(() => {
    if (!questStats?.pro_booster_until) {
      setBoosterTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const remaining = new Date(questStats.pro_booster_until).getTime() - Date.now();
      if (remaining <= 0) {
        setBoosterTimeRemaining(null);
        if (onBillingChange) {
          onBillingChange();
        }
      } else {
        const hours = Math.floor(remaining / (3600 * 1000));
        const mins = Math.floor((remaining % (3600 * 1000)) / (60 * 1000));
        const secs = Math.floor((remaining % (60 * 1000)) / 1000);
        setBoosterTimeRemaining(`${hours}h ${mins}m ${secs}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [questStats?.pro_booster_until]);

  // Automatically check & unlock Pro Champion badge if user is Pro
  useEffect(() => {
    if (isProUser || stats?.claimedDailyBonus) {
      const currentStats = getUserStats(currentUser?.id);
      const ach = currentStats.achievements.find(a => a.id === "pro_champion");
      if (ach && !ach.unlockedAt) {
        const updated = {
          ...currentStats,
          achievements: currentStats.achievements.map(a => {
            if (a.id === "pro_champion") {
              return { ...a, unlockedAt: new Date().toLocaleDateString() };
            }
            return a;
          })
        };
        saveUserStats(updated, currentUser?.id);
        setStats(updated);
      }
    }
  }, [isProUser, stats?.claimedDailyBonus, currentUser]);

  // Automatically sync local total_points if it's lower than the earned milestones
  useEffect(() => {
    if (stats) {
      const todayStr = new Date().toISOString().split('T')[0];
      const hasCheckedInToday = stats.lastActiveDate === todayStr;
      const hasStreakBonus = (stats.streak ?? 0) > 1 || (stats.monthlyMilestones?.activeDaysCount ?? 0) > 1;
      const hasClaimedDailyBonus = !!stats.claimedDailyBonus;

      let bonusQP = 0;
      if (hasCheckedInToday) bonusQP += 50;
      if (hasStreakBonus) bonusQP += 150;
      if (hasClaimedDailyBonus) bonusQP += 150;

      // Only auto-upgrade the user's permanent total_points to include daily/streak/claim bonuses if they have already begun their journey (earned at least 1 base point)
      if (stats.total_points > 0 && (stats.total_points ?? 0) < bonusQP) {
        const updated = {
          ...stats,
          total_points: bonusQP
        };
        saveUserStats(updated);
        setStats(updated);

        // Sync with server too
        if (currentUser) {
          apiFetch("/api/user-stats", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ stats: updated })
          }).catch((err) => console.warn("Failed to sync updated bonus points with server:", err));
        }
      }
    }
  }, [stats?.lastActiveDate, stats?.streak, stats?.monthlyMilestones?.activeDaysCount, stats?.claimedDailyBonus, stats?.total_points, currentUser]);

  // Listen for real-time quest update events triggered across the application
  useEffect(() => {
    const handleQuestUpdate = () => {
      loadQuests();
    };

    window.addEventListener("biznearyou_quests_updated", handleQuestUpdate);
    return () => {
      window.removeEventListener("biznearyou_quests_updated", handleQuestUpdate);
    };
  }, [currentUser]);

  // Reload stats & preferences on mount and sync with SQL database
  useEffect(() => {
    const localStats = reloadStats(currentUser?.id);
    setStats(localStats);
    setPreferredCats(getPreferredCategories());

    if (currentUser) {
      bizDebug("USER_STATS_LOAD_START", { userId: currentUser.id });
      apiFetch("/api/user-stats")
        .then((res: any) => {
          if (res.ok) {
            return res.json();
          }
          if (res.status !== 401 && res.status !== 403 && res.status !== 400) {
            bizError("USER_STATS_LOAD_ERROR", { status: res.status, url: "/api/user-stats" });
          }
          throw new Error(`Server status ${res.status}`);
        })
        .then((dbStats: any) => {
          bizDebug("USER_STATS_LOAD_SUCCESS", { hasDbStats: !!dbStats });
          if (dbStats) {
            // Merge dbStats with localStats safely
            const dbAchievements = Array.isArray(dbStats?.achievements) ? dbStats.achievements : [];
            const localAchievements = Array.isArray(localStats?.achievements) ? localStats.achievements : [];

            // Combine achievements: an achievement is unlocked if unlocked either locally OR in DB
            const mergedAchievements = (Array.isArray(localAchievements) ? localAchievements : []).map(localAch => {
              const dbAch = dbAchievements.find((a: any) => a.id === localAch.id);
              return {
                ...localAch,
                unlockedAt: localAch.unlockedAt || dbAch?.unlockedAt || null
              };
            });

            // Calculate badge points from unlocked achievements
            let badgePoints = 0;
            mergedAchievements.forEach(ach => {
              if (ach.unlockedAt) {
                switch (ach.rarity) {
                  case "Common": badgePoints += 50; break;
                  case "Uncommon": badgePoints += 100; break;
                  case "Rare": badgePoints += 250; break;
                  case "Epic": badgePoints += 500; break;
                  case "Legendary": badgePoints += 1000; break;
                  default: badgePoints += 50; break;
                }
              }
            });

            const mergedTotalPoints = Math.max(
              dbStats.total_points ?? 0,
              localStats?.total_points ?? 0,
              badgePoints
            );

            const mergedStats: UserStats = {
              streak: Math.max(localStats?.streak || 0, dbStats.streak || 1),
              lastActiveDate: dbStats.lastActiveDate || localStats?.lastActiveDate,
              total_points: mergedTotalPoints,
              monthlyMilestones: dbStats.monthlyMilestones || localStats?.monthlyMilestones,
              dailyGoals: {
                reviewed: Math.max(localStats?.dailyGoals?.reviewed || 0, dbStats.dailyGoals?.reviewed || 0),
                saved: Math.max(localStats?.dailyGoals?.saved || 0, dbStats.dailyGoals?.saved || 0),
                contacted: Math.max(localStats?.dailyGoals?.contacted || 0, dbStats.dailyGoals?.contacted || 0),
                followUp: Math.max(localStats?.dailyGoals?.followUp || 0, dbStats.dailyGoals?.followUp || 0)
              },
              achievements: mergedAchievements,
              claimedDailyBonus: localStats?.claimedDailyBonus || dbStats.claimedDailyBonus || false
            };
            
            saveUserStats(mergedStats, currentUser?.id);
            setStats(mergedStats);

            // POST merged stats back to DB to ensure parity
            apiFetch("/api/user-stats", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ stats: mergedStats })
            }).catch((err: any) => {
              setApiError("Failed to update merged user stats: " + (err.message || err));
              console.warn("Failed to update merged stats:", err);
            });
          } else {
            // First time or empty DB row, upload localStats to server
            apiFetch("/api/user-stats", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ stats: localStats })
            }).catch((err: any) => {
              setApiError("Failed to push initial user stats: " + (err.message || err));
              console.warn("Failed to push initial local stats:", err);
            });
          }
        })
        .catch((err: any) => {
          if (err.message !== "Server status 401" && err.message !== "Server status 403" && err.message !== "Server status 400") {
            bizError("USER_STATS_LOAD_ERROR", { error: err.message || err });
          }
          setApiError("Could not synchronize user stats with server: " + (err.message || err));
          console.warn("Could not sync user stats with SQL database:", err);
        });
    }
  }, [savedBusinesses, Businesses, currentUser]);

  // Combine all known businesses from search and saved to have a rich pool
  const allKnownBusinessesMap = new Map<string, BusinessBusiness>();
  Businesses.forEach(b => allKnownBusinessesMap.set(b.id, b));
  savedBusinesses.forEach(b => allKnownBusinessesMap.set(b.id, b));
  const allKnownBusinesses = Array.from(allKnownBusinessesMap.values());

  // Rank businesses with dynamic personalization boost
  const scoredBusinesses = allKnownBusinesses.map(b => {
    const prospect = analyzeBusinessProspect(b);
    const baseScore = b.Businessescore !== undefined ? b.Businessescore : prospect.score;
    const personalizedScore = getPersonalizedScore(b, baseScore);
    const explanation = getPersonalizedExplanation(b);
    return {
      business: b,
      prospect,
      baseScore,
      personalizedScore,
      explanation
    };
  }).sort((a, b) => b.personalizedScore - a.personalizedScore);

  // Recommendations: top 3 non-saved businesses (or saved if no others) that score well
  const recommendedTargets = scoredBusinesses
    .filter(item => !savedBusinesses.some(s => s.id === item.business.id))
    .slice(0, 3);

  // If we don't have enough, fill in with any high-score ones
  const recommendations = recommendedTargets.length > 0 
    ? recommendedTargets 
    : scoredBusinesses.slice(0, 3);

  // High Hiring Signal Businesses
  const highHiringBusinesses = scoredBusinesses
    .filter(item => {
      const tags = normalizeTags(item.prospect?.tags);
      const isHiring = 
        tags.includes("Currently Hiring") || 
        tags.includes("Hiring Multiple Roles") || 
        tags.includes("High Hiring Signal") || 
        item.prospect.scores.hiringScore >= 60;
      return isHiring;
    })
    .slice(0, 3);

  // Follow-ups due today (CRM stage)
  const todayStart = new Date();
  todayStart.setHours(0,0,0,0);
  const followUpsDue = savedBusinesses.filter(b => {
    if (!b.followUpAt) return false;
    const followUpDate = new Date(b.followUpAt);
    return followUpDate <= todayStart || (followUpDate.toDateString() === new Date().toDateString());
  });

  // CRM status metrics
  const crmMetrics = {
    new: savedBusinesses.filter(b => (b.pipelineStage || "New") === "New").length,
    contacted: savedBusinesses.filter(b => b.pipelineStage === "Contacted").length,
    interested: savedBusinesses.filter(b => b.pipelineStage === "Interested").length,
    followUp: savedBusinesses.filter(b => b.pipelineStage === "Follow Up").length,
    won: savedBusinesses.filter(b => b.pipelineStage === "Won").length,
  };

  const websiteGapsSavedCount = savedBusinesses.filter(b => {
    const prospect = analyzeBusinessProspect(b);
    return normalizeTags(prospect?.tags).includes("No Website Found");
  }).length;

  // Goal values - completely reasonable tasks doable entirely on the free tier
  const reviewedGoal = 5;
  const savedGoal = 2;
  const contactedGoal = 1;

  const totalGoalProgress = 
    Math.min(reviewedGoal, stats?.dailyGoals?.reviewed || 0) +
    Math.min(savedGoal, stats?.dailyGoals?.saved || 0) +
    Math.min(contactedGoal, stats?.dailyGoals?.contacted || 0);

  const totalGoalTarget = reviewedGoal + savedGoal + contactedGoal;
  const questProgressPercentage = Math.round((totalGoalProgress / totalGoalTarget) * 100);

  // Add telemetry for Dashboard component inside useEffect to prevent render spam
  useEffect(() => {
    bizDebug("DASHBOARD_RENDER", {
      userId: currentUser?.id,
      isPro: isProUser,
      questsLoading
    });
  }, [currentUser?.id, isProUser, questsLoading]);

  // Theme support classes
  const isDark = settingsAppTheme === "dark";
  const bgClass = isDark ? "bg-slate-950 text-slate-100" : "bg-white text-slate-800";
  const cardClass = isDark ? "bg-slate-900 border-slate-800 hover:border-slate-700" : "bg-white border-slate-200/80 shadow-xs hover:shadow-sm";
  const textTitleClass = isDark ? "text-white" : "text-slate-900";
  const textMutedClass = isDark ? "text-slate-400" : "text-slate-500 font-medium";

  // Enhanced XP & Level Calculations based on total_points (acts as experience points / QP)
  const basePoints = questStats?.total_points ?? stats?.total_points ?? 0;
  const todayStr = new Date().toISOString().split('T')[0];
  const hasCheckedInToday = stats?.lastActiveDate === todayStr;
  const hasStreakBonus = (stats?.streak ?? 0) > 1 || (stats?.monthlyMilestones?.activeDaysCount ?? 0) > 1;
  const hasClaimedDailyBonus = !!stats?.claimedDailyBonus;

  let bonusQP = 0;
  if (hasCheckedInToday) bonusQP += 50;
  if (hasStreakBonus) bonusQP += 150;
  if (hasClaimedDailyBonus) bonusQP += 150;

  // Only apply bonusQP towards active level points if they have actually started earning base points (scouted at least 1 target) to prevent brand new users from starting at Level 2.
  const questPoints = basePoints === 0 ? 0 : Math.max(basePoints, bonusQP);
  const currentLevel = Math.floor(Math.sqrt(questPoints / 50)) + 1;
  const xpForCurrentLevel = Math.pow(currentLevel - 1, 2) * 50;
  const xpForNextLevel = Math.pow(currentLevel, 2) * 50;
  const xpInCurrentLevel = questPoints - xpForCurrentLevel;
  const xpNeededForNextLevel = xpForNextLevel - xpForCurrentLevel;
  const levelProgressPercentage = Math.min(Math.round((xpInCurrentLevel / xpNeededForNextLevel) * 100), 100);

  return (
    <div className={`flex flex-col h-auto min-h-full p-4 sm:p-6 pb-20 lg:pb-6 max-w-6xl mx-auto space-y-6 sm:space-y-8 font-sans ${bgClass} animate-fade-in`}>
      
      {/* Visual API Error Alert Banner */}
      {apiError && (
        <div className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-2xl text-red-900 text-sm font-semibold shadow-3xs animate-fade-in">
          <div className="flex items-center gap-3 text-left">
            <span className="text-lg">⚠️</span>
            <div>
              <p className="font-extrabold tracking-tight">Sync Interrupted</p>
              <p className="text-xs text-red-650 font-medium leading-relaxed">{apiError}</p>
            </div>
          </div>
          <button 
            onClick={() => setApiError(null)}
            className="text-red-400 hover:text-red-600 transition-colors p-1 text-base font-black"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* 1. Header Greeting & Streak Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-dashed border-slate-200/80 pb-6">
        <div className="space-y-1.5 text-left w-full">
          <div className="flex items-center justify-between gap-2 pr-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">👋</span>
              <h1 className="text-xl md:text-2xl font-black tracking-tight text-slate-900 leading-tight">
                Welcome back, <span className="text-indigo-600 font-extrabold">{currentUser?.name || "Explorer"}</span>
              </h1>
            </div>
            <InAppNotificationBell onNavigate={(url) => onSetWorkspaceMode(url.includes("settings") ? "dashboard" : "dashboard")} />
          </div>
          <p className="text-[11px] sm:text-xs text-slate-500 font-semibold flex flex-wrap items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>{new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="hidden sm:inline text-slate-300">·</span>
            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>Your local search area</span>
          </p>
        </div>

        {/* BizNearYou Quest System Stats Card */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-stretch">
          {/* Points & Streak Card */}
          <div className="flex flex-col gap-2.5 bg-amber-50/80 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 p-3.5 rounded-2xl shadow-3xs text-left w-full sm:w-80">
            {/* Row 1: Daily Streak */}
            <div className="flex items-center gap-3">
              <div className="bg-amber-400 p-2 rounded-xl border border-amber-300 flex items-center justify-center shadow-xs shrink-0">
                <Flame className="w-5.5 h-5.5 fill-slate-900 text-slate-900 animate-pulse" />
              </div>
              <div className="space-y-0.5">
                <span className="text-xs font-black text-slate-900 tracking-tight">Search Streak</span>
                <p className="text-[11px] text-slate-655 font-semibold leading-relaxed">
                  <span className="text-amber-800 font-black text-sm">{questStats?.daily_streak ?? stats?.streak ?? 0} Days</span> active
                </p>
              </div>
            </div>

            {/* Row 2: Level & XP Progress Bar */}
            <div className="border-t border-amber-200/60 pt-2 space-y-1">
              <div className="flex justify-between items-center text-[10px] font-bold">
                <span className="text-indigo-900 flex items-center gap-1 font-extrabold uppercase tracking-wide">
                  👑 Rank LVL {currentLevel}
                </span>
                <span className="text-slate-500 font-mono">
                  {questPoints} / {xpForNextLevel} QP (XP)
                </span>
              </div>
              <div className="w-full bg-slate-200/80 h-1.5 rounded-full overflow-hidden border border-slate-300/40">
                <div 
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${levelProgressPercentage}%` }}
                />
              </div>
              <div className="text-[9px] text-slate-500 italic text-right font-medium">
                {xpNeededForNextLevel - xpInCurrentLevel} QP to Next Rank
              </div>
            </div>
          </div>

          {/* Live Active Booster Card */}
          {boosterTimeRemaining && (() => {
            const activeBooster = getActiveBoosterDisplay(isProUser, boosterTimeRemaining);
            return (
              <div className="flex items-center gap-3 bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/60 p-3 rounded-2xl shadow-3xs text-left animate-pulse w-full sm:w-auto">
                <div className="bg-indigo-600 p-2.5 rounded-xl border border-indigo-500 flex items-center justify-center shadow-xs text-white">
                  <Zap className="w-5 h-5 fill-white text-white" />
                </div>
                <div className="space-y-0.5">
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-black text-indigo-950 tracking-tight">
                      {activeBooster.displayName} Active!
                    </span>
                  </div>
                  <p className="text-[10.5px] text-indigo-800 font-mono font-black">
                    {boosterTimeRemaining}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* 2. BizNearYou Dynamic Quest System & Milestones Grid */}
      <TodaysScoutingQuestSection
        isProUser={isProUser || false}
        questsLoading={questsLoading}
        quests={quests}
        activeQuestTab={activeQuestTab}
        setActiveQuestTab={setActiveQuestTab}
        onUpgradeClick={onUpgradeClick || (() => {})}
        expandedQuestId={expandedQuestId}
        setExpandedQuestId={setExpandedQuestId}
        claimQuest={claimQuest}
        stats={stats}
        setIsBadgesModalOpen={setIsBadgesModalOpen}
        settingsAppTheme={settingsAppTheme || "light"}
        boosterTimeRemaining={boosterTimeRemaining}
        claimingQuestKey={claimingQuestKey}
      />

      {/* 4. Quest Log & History Timeline (1-column full width) */}
      <div className={`p-5 rounded-2xl border ${cardClass} text-left space-y-4`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-dashed border-slate-150 pb-2.5">
          <div className="space-y-0.5">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-indigo-500" />
              <span>Activity history</span>
            </h2>
            <p className="text-xs text-slate-500 font-semibold">
              A quick look at your recent progress and updates.
            </p>
          </div>
          <span className="text-[9.5px] font-black tracking-widest uppercase bg-slate-100 text-slate-550 border border-slate-200 px-2 py-0.5 rounded">
            Saved locally
          </span>
        </div>

        <div className="relative border-l border-slate-200 ml-3.5 pl-5 space-y-4 pt-1.5">
          {/* Node 1 */}
          <div className="relative">
            <div className={`absolute -left-[27px] top-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ring-4 ${
              hasCheckedInToday ? "bg-indigo-600 ring-indigo-50" : "bg-slate-300 ring-slate-100"
            }`}></div>
            <div className="space-y-0.5 text-xs text-left">
              <p className={`font-extrabold ${hasCheckedInToday ? "text-slate-900" : "text-slate-400"}`}>
                {hasCheckedInToday ? "Checked in today" : "No check-in today"}
              </p>
              <p className="text-slate-500 text-[11px] font-semibold">
                {hasCheckedInToday ? "Reviewed today's potential targets (+50 QP)" : "Open the dashboard daily to earn active check-in points."}
              </p>
            </div>
          </div>

          {/* Node 2 */}
          <div className="relative">
            <div className={`absolute -left-[27px] top-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ring-4 ${
              hasStreakBonus ? "bg-emerald-600 ring-emerald-50" : "bg-slate-300 ring-slate-100"
            }`}></div>
            <div className="space-y-0.5 text-xs text-left">
              <p className={`font-extrabold ${hasStreakBonus ? "text-slate-900" : "text-slate-400"}`}>
                {hasStreakBonus ? "Active habit streak growing" : "Streak inactive"}
              </p>
              <p className="text-slate-500 text-[11px] font-semibold">
                {hasStreakBonus ? "Great job keeping the streak going! (+150 Streak Bonus QP)" : "Maintain a multi-day search activity to activate streak bonus."}
              </p>
            </div>
          </div>

          {/* Node 3 */}
          <div className="relative">
            <div className={`absolute -left-[27px] top-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ring-4 ${
              hasClaimedDailyBonus ? "bg-amber-600 ring-amber-50" : "bg-slate-300 ring-slate-100"
            }`}></div>
            <div className="space-y-0.5 text-xs text-left">
              <p className={`font-extrabold ${hasClaimedDailyBonus ? "text-slate-900" : "text-slate-400"}`}>
                {hasClaimedDailyBonus ? "Daily bonus claimed" : "Daily bonus unclaimed"}
              </p>
              <p className="text-slate-500 text-[11px] font-semibold">
                {hasClaimedDailyBonus ? "Gained daily activity points (+150 QP)" : "Complete today's main objectives to unlock daily bonus."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Trophy Case / View All Badges Modal */}
      {isBadgesModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
            
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-slate-100 flex items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-slate-50 to-indigo-50/40">
              <div className="flex items-start sm:items-center gap-3">
                <div className="bg-amber-400 text-slate-900 p-2 sm:p-2.5 rounded-2xl shadow-sm shrink-0">
                  <Trophy className="w-5 h-5 sm:w-6 sm:h-6 fill-slate-900" />
                </div>
                <div className="text-left min-w-0">
                  <h2 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">Your Achievement Trophy Case</h2>
                  <p className="text-[11px] sm:text-xs text-slate-500 font-semibold leading-snug">
                    Earn high-reputation badges by discovering targets and advancing CRM stages.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsBadgesModalOpen(false)}
                className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer shrink-0"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Sub-Header / Filters / Search row */}
            <div className="p-4 bg-slate-50 border-b border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              {/* Badge Filters */}
              <div className="flex bg-slate-200/50 p-1 rounded-xl w-full sm:w-auto">
                {(["all", "unlocked", "locked"] as const).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setBadgeFilter(filter)}
                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider transition cursor-pointer ${
                      badgeFilter === filter
                        ? "bg-white text-indigo-600 shadow-xs"
                        : "text-slate-500 hover:text-slate-750"
                    }`}
                  >
                    {filter === "all" ? "All" : filter === "unlocked" ? "Unlocked" : "Locked"}
                  </button>
                ))}
              </div>

              {/* Badge Search */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search badge..."
                  value={badgeSearch}
                  onChange={(e) => setBadgeSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
              </div>
            </div>

            {/* Badge Grid Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-slate-50/50">
              {(() => {
                const filteredBadges = (stats?.achievements || []).filter((ach) => {
                  const matchFilter = 
                    badgeFilter === "all" ||
                    (badgeFilter === "unlocked" && ach.unlockedAt) ||
                    (badgeFilter === "locked" && !ach.unlockedAt);
                  const matchSearch = 
                    (ach?.title || "").toLowerCase().includes(badgeSearch.toLowerCase()) ||
                    (ach?.description || "").toLowerCase().includes(badgeSearch.toLowerCase());
                  return matchFilter && matchSearch;
                });

                if (filteredBadges.length === 0) {
                  return (
                    <div className="text-center py-16 text-slate-400 italic text-xs bg-white border border-slate-200 rounded-2xl">
                      No matching achievements found in your trophy case. Keep exploring!
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    {filteredBadges.map((ach) => {
                      const isUnlocked = !!ach.unlockedAt;
                      const isSecret = !!ach.isSecret && !isUnlocked;
                      const title = isSecret ? "Secret Badge" : ach.title;
                      const description = isSecret ? "A mysterious milestone. Keep exploring and completing actions to unlock this hidden achievement!" : ach.description;
                      const icon = isSecret ? "❓" : (ach.icon || "🏅");
                      
                      const target = ach.target;
                      const currentVal = target ? getAchievementProgressValue(ach.id, stats) : 0;
                      const pct = target ? Math.min(100, Math.round((currentVal / target) * 100)) : 0;

                      // Rarity color mappings
                      const rarityStyles = {
                        Common: "bg-slate-100 text-slate-600 border-slate-200",
                        Uncommon: "bg-blue-50 text-blue-600 border-blue-200",
                        Rare: "bg-purple-50 text-purple-600 border-purple-200/65",
                        Epic: "bg-amber-50 text-amber-700 border-amber-200/50",
                        Legendary: "bg-rose-50 text-rose-600 border-rose-200"
                      };
                      const rarityClass = rarityStyles[ach.rarity as keyof typeof rarityStyles] || rarityStyles.Common;

                      return (
                        <div
                          key={ach.id}
                          className={`p-3.5 sm:p-4 rounded-xl border bg-white flex flex-col justify-between transition-all duration-300 relative overflow-hidden shadow-2xs hover:shadow-sm ${
                            isUnlocked
                              ? "border-indigo-150 ring-1 ring-indigo-500/5 bg-gradient-to-br from-white to-indigo-50/10"
                              : "border-slate-200/80 opacity-75"
                          }`}
                        >
                          <div className="flex items-start gap-3 sm:gap-4 flex-1">
                            <span className={`text-3xl shrink-0 p-2 bg-slate-50 rounded-xl flex items-center justify-center h-12 w-12 border border-slate-100/50 ${!isUnlocked ? "opacity-50 grayscale" : ""}`}>
                              {icon}
                            </span>
                            <div className="text-left space-y-1 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <h4 className="font-extrabold text-xs sm:text-sm text-slate-900 leading-tight flex items-center gap-1.5 min-w-0 max-w-full">
                                  <span className="truncate">{title}</span>
                                  {isUnlocked && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                                </h4>
                                <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded border leading-none shrink-0 ${rarityClass}`}>
                                  {ach.rarity || "Common"}
                                </span>
                              </div>
                              <p className="text-[11px] sm:text-xs text-slate-500 leading-snug font-medium line-clamp-2">
                                {description}
                              </p>
                            </div>
                          </div>

                          {/* Progress bar or lock/unlock status */}
                          <div className="pt-3.5 mt-3.5 border-t border-slate-100/90">
                            {isUnlocked ? (
                              <p className="text-[9px] sm:text-[10px] text-emerald-600 font-bold font-mono flex items-center gap-1 justify-between">
                                <span className="uppercase tracking-wider">🎉 Completed</span>
                                <span>Unlocked {ach.unlockedAt}</span>
                              </p>
                            ) : target ? (
                              <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold font-mono text-slate-400">
                                  <span className="uppercase tracking-wider">Progress</span>
                                  <span>{currentVal} / {target}</span>
                                </div>
                                <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden border border-slate-200/30">
                                  <div 
                                    className="bg-indigo-500 h-full rounded-full transition-all duration-300"
                                    style={{ width: `${pct}%` }}
                                  ></div>
                                </div>
                              </div>
                            ) : (
                              <p className="text-[9px] sm:text-[10px] text-slate-400 font-bold font-mono flex items-center gap-1 uppercase tracking-wider">
                                <span>🔒 Locked</span>
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer / Completion Stats */}
            <div className="p-4 sm:p-6 border-t border-slate-150 bg-slate-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="text-left space-y-1">
                <p className="text-[10px] sm:text-xs font-black text-slate-700">Trophy Case Progress</p>
                <div className="flex items-center gap-2">
                  <div className="w-32 sm:w-40 bg-slate-200 h-2 rounded-full overflow-hidden border border-slate-300">
                    <div 
                      className="bg-indigo-600 h-full rounded-full transition-all"
                      style={{ width: `${Math.round(((stats?.achievements?.filter(a => a?.unlockedAt)?.length || 0) / (stats?.achievements?.length || 41)) * 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-[10px] sm:text-[11px] font-mono font-black text-indigo-600 shrink-0">
                    {stats?.achievements?.filter(a => a?.unlockedAt)?.length || 0} / {stats?.achievements?.length || 0} Unlocked
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsBadgesModalOpen(false)}
                className="w-full sm:w-auto px-5 py-2 sm:py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs rounded-xl shadow transition cursor-pointer text-center"
              >
                Close Shelf
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
